import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import hostelService from '../services/hostelService';
import { formatDateTimeIST } from '../utils/istTimeUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const isSubscriptionExpired = (expiryDateStr) => {
  if (!expiryDateStr) return false;
  const expStr = expiryDateStr.substring(0, 10);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;
  return expStr < todayStr;
};

const CardImageSlider = ({ hostel, baseUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const getImages = () => {
    const list = [];
    if (hostel.image_url) {
      list.push(hostel.image_url);
    }
    if (hostel.gallery_images) {
      let gallery = [];
      if (Array.isArray(hostel.gallery_images)) {
        gallery = hostel.gallery_images;
      } else if (typeof hostel.gallery_images === 'string') {
        try {
          gallery = JSON.parse(hostel.gallery_images);
        } catch (e) {}
      }
      gallery.forEach((img) => {
        if (img && !list.includes(img)) {
          list.push(img);
        }
      });
    }
    return list;
  };

  const images = getImages();

  if (images.length === 0) {
    return '🏢';
  }

  const getFullUrl = (url) => {
    return url.startsWith('http') ? url : `${baseUrl.replace('/api', '')}${url}`;
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="web-card-slider" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <img
        src={getFullUrl(images[currentIndex])}
        alt={hostel.name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.parentElement.innerHTML = '🏢';
        }}
      />
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            style={{
              position: 'absolute',
              top: '50%',
              left: '8px',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              fontSize: '14px',
              outline: 'none',
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(0,0,0,0.8)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(0,0,0,0.5)'}
          >
            &#10094;
          </button>
          <button
            onClick={handleNext}
            style={{
              position: 'absolute',
              top: '50%',
              right: '8px',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              fontSize: '14px',
              outline: 'none',
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(0,0,0,0.8)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(0,0,0,0.5)'}
          >
            &#10095;
          </button>

          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '4px',
              zIndex: 2,
            }}
          >
            {images.map((_, idx) => (
              <span
                key={idx}
                style={{
                  width: currentIndex === idx ? '12px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  background: currentIndex === idx ? '#84CC16' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const HostelPage = () => {
  // Read initial tab from URL hash (e.g., /hostel#admissions)
  const getInitialTab = () => {
    const hash = window.location.hash.slice(1); // Remove the # prefix
    const validTabs = ['locations', 'admissions', 'leaves', 'bookings'];
    return validTabs.includes(hash) ? hash : 'locations';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [admissions, setAdmissions] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [locations, setLocations] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingHostelId, setEditingHostelId] = useState(null);
  const [showAmenitiesModal, setShowAmenitiesModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRoomsModal, setShowRoomsModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [admissionHistory, setAdmissionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Room management states
  const [hostelRooms, setHostelRooms] = useState([]);
  const [newRoom, setNewRoom] = useState({ building: '', room_number: '', capacity: '' });
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: 'Nashik',
    state: 'Maharashtra',
    pincode: '',
    latitude: '',
    longitude: '',
    hostel_type: 'boys',
    total_rooms: '',
    monthly_rent: '',
    yearly_rent: '',
    deposit_amount: '',
    contact_phone: '',
    contact_email: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [amenities, setAmenities] = useState([]);
  const [newAmenity, setNewAmenity] = useState('');
  
  // Gallery image states
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setGalleryFiles(prev => [...prev, ...files]);
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setGalleryPreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeNewGalleryImage = (index) => {
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingGalleryImage = (url) => {
    setExistingGallery(prev => prev.filter(item => item !== url));
  };
  
  // Booking filter states
  const [bookingFilterStartDate, setBookingFilterStartDate] = useState('');
  const [bookingFilterEndDate, setBookingFilterEndDate] = useState('');
  
  // Student search states
  const [bookingSearch, setBookingSearch] = useState('');
  const [admissionSearch, setAdmissionSearch] = useState('');
  const [admissionFilter, setAdmissionFilter] = useState(null); // 'active' | 'ended'
  const [leaveSearch, setLeaveSearch] = useState('');
  const tableRef = useRef(null);

  const getFilteredBookings = () => {
    return bookingRequests.filter(booking => {
      // Search filter
      if (bookingSearch.trim()) {
        const q = bookingSearch.toLowerCase();
        const name = (booking.student_name || '').toLowerCase();
        const phone = (booking.student_phone || '').toLowerCase();
        if (!name.includes(q) && !phone.includes(q)) return false;
      }
      // Date filter
      if (!bookingFilterStartDate && !bookingFilterEndDate) return true;
      const bookingDate = new Date(booking.created_at);
      const startDate = bookingFilterStartDate ? new Date(bookingFilterStartDate) : null;
      const endDate = bookingFilterEndDate ? new Date(bookingFilterEndDate) : null;
      if (startDate && bookingDate < startDate) return false;
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (bookingDate > endOfDay) return false;
      }
      return true;
    });
  };

  const handlePrintToPDF = () => {
    const element = tableRef.current;
    if (!element) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    const filteredBookings = getFilteredBookings();
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hostel Booking Requests - Sai Mess & Hostel</title>
        <style>
          * { margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: white; }
          
          .header {
            display: flex;
            align-items: center;
            gap: 15px;
            border-bottom: 3px solid #007bff;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          
          .logo {
            width: 80px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          
          .logo img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          
          .header-text h1 {
            color: #004085;
            font-size: 24px;
            margin: 0;
            padding: 0;
          }
          
          .header-text p {
            color: #666;
            font-size: 12px;
            margin: 3px 0 0 0;
          }
          
          h2 {
            text-align: center;
            color: #333;
            font-size: 18px;
            margin: 20px 0 10px 0;
          }
          
          .filter-info {
            background: #f5f5f5;
            padding: 12px;
            margin: 10px 0;
            border-radius: 4px;
            border-left: 4px solid #007bff;
            font-size: 13px;
            line-height: 1.6;
          }
          
          .filter-info strong {
            color: #004085;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
            font-size: 12px;
          }
          
          thead {
            background: #007bff;
            color: white;
          }
          
          th, td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          
          tr:nth-child(even) {
            background: #f9f9f9;
          }
          
          .status-badge {
            padding: 4px 8px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
            text-align: center;
            display: inline-block;
            width: 70px;
          }
          
          .status-approved {
            background: #d4edda;
            color: #155724;
          }
          
          .status-rejected {
            background: #f8d7da;
            color: #721c24;
          }
          
          .status-pending {
            background: #fff3cd;
            color: #856404;
          }
          
          .footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 11px;
            color: #666;
          }
          
          .footer p {
            margin: 5px 0;
          }
          
          @media print {
            body { margin: 0; padding: 10px; }
            .header { page-break-after: avoid; }
            table { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <!-- Header with Logo and Name -->
        <div class="header">
          <div class="logo">
            <img src="/SaiNiwasPayingGuest.png" alt="Sai Niwas Logo" />
          </div>
          <div class="header-text">
            <h1>🏢 Sai Mess & Hostel</h1>
            <p>Booking Management System</p>
          </div>
        </div>
        
        <!-- Report Title -->
        <h2>Hostel Booking Requests Report</h2>
        
        <!-- Filter Info -->
        <div class="filter-info">
          <strong>📋 Report Details:</strong><br/>
          <strong>Generated:</strong> ${new Date().toLocaleString()}<br/>
          ${bookingFilterStartDate ? `<strong>From Date:</strong> ${bookingFilterStartDate}<br/>` : ''}
          ${bookingFilterEndDate ? `<strong>To Date:</strong> ${bookingFilterEndDate}<br/>` : ''}
          <strong>Total Records:</strong> ${filteredBookings.length}
        </div>
        
        <!-- Data Table -->
        <table>
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Hostel Name</th>
              <th>Plan Type</th>
              <th>Phone</th>
              <th>Requested Date & Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredBookings.length > 0 ? filteredBookings.map(booking => `
              <tr>
                <td>${booking.student_name || 'N/A'}</td>
                <td>${booking.hostel_name || 'N/A'}</td>
                <td>${booking.plan_type || 'N/A'}</td>
                <td>${booking.student_phone || 'N/A'}</td>
                <td>${formatDateTimeIST(booking.created_at)}</td>
                <td><span class="status-badge status-${booking.status}">${booking.status}</span></td>
              </tr>
            `).join('') : '<tr><td colspan="6" style="text-align: center; color: #999;">No booking requests found for the selected date range.</td></tr>'}
          </tbody>
        </table>
        
        <!-- Footer -->
        <div class="footer">
          <p><strong>Sai Mess & Hostel Management System</strong></p>
          <p>This is an auto-generated report. For official use only.</p>
          <p>© 2026 All Rights Reserved</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Print after content is loaded
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const sortByActivity = (arr) => [...arr].sort((a, b) => {
        const aTime = a.updated_at || a.created_at || '';
        const bTime = b.updated_at || b.created_at || '';
        return bTime.localeCompare(aTime);
      });
      if (activeTab === 'admissions') {
        const response = await hostelService.getAdmissions();
        setAdmissions(sortByActivity(response.data?.data || []));
      } else if (activeTab === 'leaves') {
        const response = await hostelService.getLeaves();
        setLeaves(sortByActivity(response.data?.data || []));
      } else if (activeTab === 'locations') {
        const response = await hostelService.getLocations();
        setLocations(response.data?.data || []);
      } else if (activeTab === 'bookings') {
        const response = await hostelService.getBookingRequests();
        setBookingRequests(sortByActivity(response.data?.data || []));
      }
    } catch (err) {
      console.error('Error loading data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load data. Please ensure the backend server is running.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (showDetailsModal) {
          setShowDetailsModal(false);
        } else if (showEditForm) {
          setShowEditForm(false);
        } else if (showAmenitiesModal) {
          setShowAmenitiesModal(false);
        } else if (showAddForm) {
          setShowAddForm(false);
        } else if (showRoomsModal) {
          setShowRoomsModal(false);
        } else if (showDocumentsModal) {
          setShowDocumentsModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showDetailsModal, showEditForm, showAmenitiesModal, showAddForm, showRoomsModal, showDocumentsModal]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddHostel = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      let imageUrl = null;
      
      // Upload image if provided
      if (imageFile) {
        const uploadResponse = await hostelService.uploadHostelImage(imageFile);
        if (uploadResponse.data?.success) {
          imageUrl = uploadResponse.data.image_url;
        }
      }
      
      // Upload gallery images if provided
      const uploadedGalleryUrls = [];
      for (const file of galleryFiles) {
        const uploadResponse = await hostelService.uploadHostelImage(file);
        if (uploadResponse.data?.success) {
          uploadedGalleryUrls.push(uploadResponse.data.image_url);
        }
      }
      
      // Create hostel
      const hostelData = {
        ...formData,
        image_url: imageUrl,
        gallery_images: uploadedGalleryUrls,
        amenities: amenities,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        yearly_rent: parseFloat(formData.yearly_rent) || 0,
        deposit_amount: parseFloat(formData.deposit_amount) || 0,
        latitude: parseFloat(formData.latitude) || null,
        longitude: parseFloat(formData.longitude) || null,
      };
      
      const response = await hostelService.createLocation(hostelData);
      
      if (response.data?.success) {
        alert('Hostel added successfully!');
        setShowAddForm(false);
        setFormData({
          name: '',
          address: '',
          city: 'Nashik',
          state: 'Maharashtra',
          pincode: '',
          latitude: '',
          longitude: '',
          hostel_type: 'boys',
          total_rooms: '',
          monthly_rent: '',
          yearly_rent: '',
          deposit_amount: '',
          contact_phone: '',
          contact_email: '',
        });
        setImageFile(null);
        setImagePreview(null);
        setGalleryFiles([]);
        setGalleryPreviews([]);
        setExistingGallery([]);
        setAmenities([]);
        loadData();
      }
    } catch (err) {
      console.error('Error adding hostel:', err);
      alert('Failed to add hostel. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditHostel = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      // Validate required fields
      if (!formData.name || !formData.address) {
        alert('Please fill in all required fields (Name, Address)');
        setUploading(false);
        return;
      }
      
      // Validate monthly_rent is provided
      if (!formData.monthly_rent || parseFloat(formData.monthly_rent) < 0) {
        alert('Please enter a valid monthly rent amount');
        setUploading(false);
        return;
      }
      
      let imageUrl = formData.image_url;
      
      // Upload new image if provided
      if (imageFile) {
        const uploadResponse = await hostelService.uploadHostelImage(imageFile);
        if (uploadResponse.data?.success) {
          imageUrl = uploadResponse.data.image_url;
        }
      }
      
      // Parse and validate coordinates
      let latitude = null;
      let longitude = null;
      
      if (formData.latitude && formData.latitude.toString().trim() !== '') {
        const lat = parseFloat(formData.latitude);
        if (!isNaN(lat)) {
          latitude = lat;
        }
      }
      
      if (formData.longitude && formData.longitude.toString().trim() !== '') {
        const lon = parseFloat(formData.longitude);
        if (!isNaN(lon)) {
          longitude = lon;
        }
      }
      
      // Upload new gallery images if provided
      const uploadedGalleryUrls = [];
      for (const file of galleryFiles) {
        const uploadResponse = await hostelService.uploadHostelImage(file);
        if (uploadResponse.data?.success) {
          uploadedGalleryUrls.push(uploadResponse.data.image_url);
        }
      }
      
      const finalGallery = [...existingGallery, ...uploadedGalleryUrls];
      
      // Update hostel - exclude total_rooms as it's auto-calculated
      const hostelData = {
        name: formData.name,
        address: formData.address,
        city: formData.city || 'Nashik',
        state: formData.state || 'Maharashtra',
        pincode: formData.pincode || '',
        latitude: latitude,
        longitude: longitude,
        hostel_type: formData.hostel_type || 'boys',
        monthly_rent: parseFloat(formData.monthly_rent),
        yearly_rent: formData.yearly_rent ? parseFloat(formData.yearly_rent) : 0,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : 0,
        contact_phone: formData.contact_phone || '',
        contact_email: formData.contact_email || '',
        image_url: imageUrl,
        gallery_images: finalGallery,
        amenities: amenities,
      };
      
      console.log('Sending hostel data:', hostelData);
      
      const response = await hostelService.updateLocation(editingHostelId, hostelData);
      
      if (response.data?.success) {
        alert(`${formData.name} Updated Successfully`);
        setShowEditForm(false);
        setEditingHostelId(null);
        setFormData({
          name: '',
          address: '',
          city: 'Nashik',
          state: 'Maharashtra',
          pincode: '',
          latitude: '',
          longitude: '',
          hostel_type: 'boys',
          total_rooms: '',
          monthly_rent: '',
          yearly_rent: '',
          deposit_amount: '',
          contact_phone: '',
          contact_email: '',
        });
        setImageFile(null);
        setImagePreview(null);
        setGalleryFiles([]);
        setGalleryPreviews([]);
        setExistingGallery([]);
        setAmenities([]);
        setHostelRooms([]);
        loadData();
      }
    } catch (err) {
      console.error('Error updating hostel:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update hostel. Please try again.';
      alert(`Error: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const openEditForm = async (hostel) => {
    setEditingHostelId(hostel.id);
    setFormData({
      name: hostel.name,
      address: hostel.address,
      city: hostel.city,
      state: hostel.state,
      pincode: hostel.pincode || '',
      latitude: hostel.latitude || '',
      longitude: hostel.longitude || '',
      hostel_type: hostel.hostel_type,
      total_rooms: hostel.total_rooms || '',
      monthly_rent: hostel.monthly_rent || '',
      yearly_rent: hostel.yearly_rent || '',
      deposit_amount: hostel.deposit_amount || '',
      contact_phone: hostel.contact_phone || '',
      contact_email: hostel.contact_email || '',
      image_url: hostel.image_url,
    });
    if (hostel.image_url) {
      setImagePreview(hostel.image_url.startsWith('http') ? hostel.image_url : `${API_BASE_URL.replace('/api', '')}${hostel.image_url}`);
    }
    setExistingGallery(hostel.gallery_images || []);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setAmenities(typeof hostel.amenities === 'string' ? JSON.parse(hostel.amenities) : hostel.amenities || []);
    
    // Load rooms for this hostel
    try {
      const response = await hostelService.getRooms(hostel.id);
      if (response.data?.success) {
        setHostelRooms(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading rooms:', err);
    }
    
    setNewRoom({ building: '', room_number: '', capacity: '' });
    setShowEditForm(true);
  };

  const handleDeleteHostel = async (hostel) => {
    if (window.confirm(`Are you sure you want to delete this hostel?\n\n${hostel.name}`)) {
      try {
        const response = await hostelService.deleteLocation(hostel.id);
        console.log('Delete response:', response);
        console.log('Response data:', response.data);
        
        // Check if deletion was successful
        if (response?.data?.success === true) {
          alert(`Hostel "${hostel.name}" deleted successfully!`);
          loadData();
        } else {
          const errorMsg = response?.data?.message || 'Failed to delete hostel';
          alert(`Error: ${errorMsg}`);
        }
      } catch (err) {
        console.error('Error deleting hostel:', err);
        const errorMsg = err.response?.data?.message || err.message || 'Failed to delete hostel. Please try again.';
        alert(`Error: ${errorMsg}`);
      }
    }
  };

  const loadAmenities = async (hostel) => {
    try {
      const response = await hostelService.getAmenities(hostel.id);
      if (response.data?.success) {
        setAmenities(response.data.amenities || []);
        setSelectedHostel(hostel);
        setShowAmenitiesModal(true);
      }
    } catch (err) {
      console.error('Error loading amenities:', err);
      alert('Failed to load amenities.');
    }
  };

  const handleAddAmenity = async () => {
    if (!newAmenity.trim()) {
      alert('Please enter an amenity name');
      return;
    }
    
    try {
      const response = await hostelService.addAmenity(selectedHostel.id, newAmenity);
      if (response.data?.success) {
        setAmenities(response.data.amenities || []);
        setNewAmenity('');
      }
    } catch (err) {
      console.error('Error adding amenity:', err);
      alert('Failed to add amenity.');
    }
  };

  const handleRemoveAmenity = async (amenity) => {
    try {
      const response = await hostelService.removeAmenity(selectedHostel.id, amenity);
      if (response.data?.success) {
        setAmenities(response.data.amenities || []);
      }
    } catch (err) {
      console.error('Error removing amenity:', err);
      alert('Failed to remove amenity.');
    }
  };

  const loadHostelRooms = async (hostel) => {
    try {
      const response = await hostelService.getRooms(hostel.id);
      if (response.data?.success) {
        setHostelRooms(response.data.data || []);
        setSelectedHostel(hostel);
        setShowRoomsModal(true);
        setNewRoom({ building: '', room_number: '', capacity: '' });
      }
    } catch (err) {
      console.error('Error loading rooms:', err);
      alert('Failed to load rooms.');
    }
  };

  const handleAddRoom = async () => {
    if (!newRoom.building || !newRoom.room_number || !newRoom.capacity) {
      alert('Please fill in all fields (Building, Room Number, Bed Count)');
      return;
    }
    
    try {
      const response = await hostelService.addRoom(selectedHostel.id, {
        building: newRoom.building,
        room_number: newRoom.room_number,
        capacity: parseInt(newRoom.capacity),
        available_beds: parseInt(newRoom.capacity)
      });
      if (response.data?.success) {
        setHostelRooms([...hostelRooms, response.data.data]);
        setNewRoom({ building: '', room_number: '', capacity: '' });
        alert('Room added successfully!');
        // Reload hostel data to update total_rooms
        loadData();
      }
    } catch (err) {
      console.error('Error adding room:', err);
      alert('Failed to add room.');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room?')) {
      try {
        const response = await hostelService.deleteRoom(roomId);
        if (response.data?.success) {
          setHostelRooms(hostelRooms.filter(r => r.id !== roomId));
          alert('Room deleted successfully!');
          // Reload hostel data to update total_rooms
          loadData();
        }
      } catch (err) {
        console.error('Error deleting room:', err);
        alert('Failed to delete room.');
      }
    }
  };

  const handleAddRoomInEditForm = async () => {
    if (!newRoom.building || !newRoom.room_number || !newRoom.capacity) {
      alert('Please fill in all fields (Building, Room Number, Bed Count)');
      return;
    }
    
    try {
      const response = await hostelService.addRoom(editingHostelId, {
        building: newRoom.building,
        room_number: newRoom.room_number,
        capacity: parseInt(newRoom.capacity),
        available_beds: parseInt(newRoom.capacity)
      });
      if (response.data?.success) {
        setHostelRooms([...hostelRooms, response.data.data]);
        setNewRoom({ building: '', room_number: '', capacity: '' });
      }
    } catch (err) {
      console.error('Error adding room:', err);
      alert('Failed to add room.');
    }
  };

  const handleUpdateRoomBeds = async (roomId, newCapacity) => {
    try {
      const response = await hostelService.updateRoom(roomId, {
        capacity: parseInt(newCapacity),
        available_beds: parseInt(newCapacity)
      });
      if (response.data?.success) {
        setHostelRooms(hostelRooms.map(r => r.id === roomId ? { ...r, capacity: parseInt(newCapacity), available_beds: parseInt(newCapacity) } : r));
      }
    } catch (err) {
      console.error('Error updating room:', err);
      alert('Failed to update room.');
    }
  };

  const handleDeleteRoomInEditForm = (roomId) => {
    if (window.confirm('Are you sure you want to delete this room?')) {
      handleDeleteRoom(roomId);
    }
  };

  const handleLeaveAction = async (id, action) => {
    try {
      await hostelService.updateLeaveStatus(id, { status: action });
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      alert('Failed to update leave status');
    }
  };

  const handleBookingAction = async (id, status) => {
    try {
      await hostelService.updateBookingStatus(id, { status });
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      alert('Failed to update booking status');
    }
  };

  const handleVerifyCashPayment = async (id) => {
    if (!window.confirm('Are you sure you want to verify this cash payment?')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/hostel/admin/admission/${id}/verify-cash-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to verify cash payment');
      }
      
      setLoading(false);
      alert('Cash payment verified successfully!');
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      setLoading(false);
      alert(`Failed to verify cash payment: ${err.message}`);
      console.error('Error verifying cash payment:', err);
    }
  };

  const handleSyncPaymentStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/hostel/admin/admissions/sync-payment-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      setLoading(false);
      if (response.ok && result.success) {
        const count = result.fixed_admission_ids?.length || 0;
        alert(count > 0 ? `✅ Synced ${count} admission(s) whose payments were already verified.` : '✅ All admissions are already up to date.');
        if (count > 0) {
          loadData();
          window.dispatchEvent(new Event('refreshPendingCounts'));
        }
      } else {
        alert(`Sync failed: ${result.message}`);
      }
    } catch (err) {
      setLoading(false);
      alert(`Sync error: ${err.message}`);
    }
  };

  const handleConfirmAdmission = async (id) => {
    if (!window.confirm('Are you sure you want to confirm this admission? Payment has been verified.')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/hostel/admin/admission/${id}/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_notes: 'Admission confirmed after verification'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to confirm admission');
      }
      
      setLoading(false);
      alert('Admission confirmed successfully!');
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      setLoading(false);
      alert(`Failed to confirm admission: ${err.message}`);
      console.error('Error confirming admission:', err);
    }
  };

  const handleViewAdmissionDetails = (id) => {
    const admission = admissions.find(a => a.id === id);
    if (admission) {
      setSelectedAdmission(admission);
      setShowDocumentsModal(true);
      fetchAdmissionHistory(admission.student_id);
    }
  };

  const fetchAdmissionHistory = async (studentId) => {
    if (!studentId) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('admin_access_token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/history/admin/student/${studentId}?type=hostel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setAdmissionHistory(data.data || []);
    } catch (e) {
      console.error('Failed to load admission history', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const commonAmenities = [
    'WiFi', 'AC', 'Hot Water', 'Mess', 'Gym', 'Sports Room', 'TV Room', 
    'Power Backup', 'Laundry', 'CCTV', 'Warden', 'Library', '24/7 Security'
  ];

  return (
    <Layout title="Hostel Management">
      <style>{`
        .tabs { display: flex; gap: 8px; margin-bottom: 24px; border-bottom: 1px solid #e5e7eb; padding-bottom: 12px; flex-wrap: wrap; }
        .tab { padding: 10px 20px; border: none; background: none; font-size: 14px; font-weight: 500; color: #6b7280; cursor: pointer; border-radius: 12px; transition: all 0.3s ease; }
        .tab:hover { background: #f3f4f6; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1); }
        .tab.active { background: #f7fee7; color: #65a30d; box-shadow: 0 4px 12px -2px rgba(132, 204, 22, 0.2); }
        .stats-row { display: flex; gap: 20px; margin-bottom: 24px; flex-wrap: wrap; }
        .stat-card { background: #f9fafb; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); flex: 1; min-width: 150px; box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.3s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -2px rgba(99, 102, 241, 0.2), 0 12px 24px -5px rgba(0, 0, 0, 0.08); }
        .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 8px; text-transform: uppercase; }
        .stat-card .value { font-size: 28px; font-weight: 600; color: #1f2937; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; font-size: 14px; }
        .data-table tr:hover { background: #f9fafb; }
        .btn { padding: 6px 12px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; font-size: 13px; transition: all 0.2s; }
        .btn:hover { transform: translateY(-1px); }
        .btn-success { background: #d1fae5; color: #047857; }
        .btn-success:hover { background: #a7f3d0; }
        .btn-danger { background: #fee2e2; color: #dc2626; }
        .btn-danger:hover { background: #fecaca; }
        .btn-primary { background: #84cc16; color: white; }
        .btn-primary:hover { background: #65a30d; }
        .btn-secondary { background: #e5e7eb; color: #374151; }
        .btn-secondary:hover { background: #d1d5db; }
        .btn-info { background: #93c5fd; color: #1e40af; }
        .btn-info:hover { background: #60a5fa; }
        .btn-small { padding: 4px 8px; font-size: 12px; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
        .status-active { background: #d1fae5; color: #047857; }
        .status-pending { background: #fef3c7; color: #b45309; }
        .status-warning { background: #fed7aa; color: #9a3412; }
        .status-approved { background: #dbeafe; color: #1d4ed8; }
        .status-confirmed { background: #dbeafe; color: #1d4ed8; }
        .status-rejected { background: #fee2e2; color: #dc2626; }
        .status-cancelled { background: #fee2e2; color: #dc2626; }
        .status-available { background: #d1fae5; color: #047857; }
        .status-occupied { background: #fee2e2; color: #dc2626; }
        .status-boys { background: #dbeafe; color: #1d4ed8; }
        .status-girls { background: #fce7f3; color: #be185d; }
        .status-co-ed { background: #e0e7ff; color: #5b21b6; }
        .actions { display: flex; gap: 8px; }
        .room-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
        .room-card { background: white; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); box-shadow: 0 6px 12px -2px rgba(59, 130, 246, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.3s ease; }
        .room-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -2px rgba(59, 130, 246, 0.2), 0 12px 24px -5px rgba(0, 0, 0, 0.08); }
        .room-card h4 { margin: 0 0 8px; font-size: 16px; color: #1f2937; }
        .room-card p { margin: 4px 0; font-size: 13px; color: #6b7280; }
        .hostel-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }
        .hostel-card { background: white; border-radius: 16px; overflow: hidden; border: 1px solid rgba(0, 0, 0, 0.04); box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.3s ease; }
        .hostel-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px -4px rgba(132, 204, 22, 0.25), 0 15px 30px -8px rgba(0, 0, 0, 0.1); }
        .hostel-card-img { width: 100%; height: 180px; object-fit: cover; background: linear-gradient(135deg, #84cc16, #65a30d); display: flex; align-items: center; justify-content: center; color: white; font-size: 48px; }
        .hostel-card-body { padding: 20px; }
        .hostel-card-body h4 { margin: 0 0 8px; font-size: 18px; color: #1f2937; }
        .hostel-card-body p { margin: 4px 0; font-size: 13px; color: #6b7280; }
        .hostel-card-body .price { font-size: 18px; font-weight: 600; color: #84cc16; margin-top: 12px; }
        .hostel-card-body .rating { display: flex; align-items: center; gap: 4px; color: #f59e0b; font-weight: 500; }
        .amenities { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
        .amenity-tag { padding: 4px 8px; background: #f3f4f6; border-radius: 6px; font-size: 11px; color: #6b7280; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
        .table-container { overflow-x: auto; }
        .add-btn { background: #84cc16; color: white; padding: 10px 20px; border-radius: 6px; border: none; cursor: pointer; font-weight: 500; }
        .add-btn:hover { background: #65a30d; }
        .form-container { background: white; padding: 24px; border-radius: 12px; border: 1px solid rgba(0, 0, 0, 0.04); margin-bottom: 24px; }
        .form-group { margin-bottom: 16px; }
        .form-label { display: block; margin-bottom: 8px; font-weight: 500; color: #374151; font-size: 14px; }
        .form-input { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; }
        .form-input:focus { outline: none; border-color: #84cc16; box-shadow: 0 0 0 3px rgba(132, 204, 22, 0.1); }
        .form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
        .image-preview { width: 100%; max-width: 200px; height: 200px; border-radius: 8px; object-fit: cover; margin-top: 10px; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; z-index: 99999; pointer-events: auto; }
        .modal-content { background: white; border-radius: 12px; padding: 24px; max-width: 1200px; width: 95%; max-height: 95vh; overflow-y: auto; z-index: 9999; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280; }
        .amenity-list { display: flex; flex-wrap: wrap; gap: 8px; margin: 16px 0; }
        .amenity-item { display: flex; align-items: center; gap: 8px; background: #f3f4f6; padding: 8px 12px; border-radius: 6px; }
        .amenity-remove { background: none; border: none; color: #dc2626; cursor: pointer; font-weight: bold; }
      `}</style>

      <div className="tabs">
        <button className={`tab ${activeTab === 'locations' ? 'active' : ''}`} onClick={() => setActiveTab('locations')}>Hostels</button>
        <button className={`tab ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>Booking Requests</button>
        <button className={`tab ${activeTab === 'admissions' ? 'active' : ''}`} onClick={() => setActiveTab('admissions')}>Admissions</button>
        <button className={`tab ${activeTab === 'leaves' ? 'active' : ''}`} onClick={() => setActiveTab('leaves')}>Leave Requests</button>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>Total Hostels</h3>
          <div className="value">{locations.length}</div>
        </div>
        <div className="stat-card">
          <h3>Pending Bookings</h3>
          <div className="value">{bookingRequests.filter(b => b.status === 'pending').length}</div>
        </div>
        <div className="stat-card">
          <h3>Total Admissions</h3>
          <div className="value">{admissions.filter(a => a.admission_status === 'completed').length}</div>
        </div>
        <div
          onClick={() => setAdmissionFilter(prev => prev === 'active' ? null : 'active')}
          className="stat-card"
          style={{ cursor: 'pointer', border: admissionFilter === 'active' ? '2px solid #16a34a' : '1px solid rgba(0,0,0,0.04)', background: admissionFilter === 'active' ? '#f0fdf4' : '#f9fafb', boxShadow: admissionFilter === 'active' ? '0 0 0 3px rgba(22,163,74,0.12)' : undefined, position: 'relative' }}
        >
          {admissionFilter === 'active' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#16a34a', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
          <h3 style={{ color: admissionFilter === 'active' ? '#16a34a' : undefined }}>Hostel Active</h3>
          <div className="value" style={{ color: '#16a34a' }}>{admissions.filter(a => a.valid_till && !isSubscriptionExpired(a.valid_till)).length}</div>
        </div>
        <div
          onClick={() => setAdmissionFilter(prev => prev === 'ended' ? null : 'ended')}
          className="stat-card"
          style={{ cursor: 'pointer', border: admissionFilter === 'ended' ? '2px solid #dc2626' : '1px solid rgba(0,0,0,0.04)', background: admissionFilter === 'ended' ? '#fef2f2' : '#f9fafb', boxShadow: admissionFilter === 'ended' ? '0 0 0 3px rgba(220,38,38,0.12)' : undefined, position: 'relative' }}
        >
          {admissionFilter === 'ended' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#dc2626', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
          <h3 style={{ color: admissionFilter === 'ended' ? '#dc2626' : undefined }}>Subscription Ended</h3>
          <div className="value" style={{ color: '#b91c1c' }}>{admissions.filter(a => a.valid_till && isSubscriptionExpired(a.valid_till)).length}</div>
        </div>
        <div className="stat-card">
          <h3>Total Beds</h3>
          <div className="value">{locations.reduce((sum, hostel) => sum + (hostel.available_beds || 0), 0)}</div>
        </div>
        <div className="stat-card">
          <h3>Pending Leaves</h3>
          <div className="value">{leaves.filter(l => l.status === 'pending').length}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : activeTab === 'locations' ? (
        <>
          {!showAddForm && !showEditForm ? (
            <button className="add-btn" onClick={() => setShowAddForm(true)}>+ Add New Hostel</button>
          ) : null}

          {showAddForm && (
            <div className="form-container">
              <h3 style={{ marginTop: 0 }}>Add New Hostel Location</h3>
              <form onSubmit={handleAddHostel}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Hostel Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hostel Type</label>
                    <select
                      className="form-input"
                      name="hostel_type"
                      value={formData.hostel_type}
                      onChange={handleFormChange}
                    >
                      <option value="boys">Boys</option>
                      <option value="girls">Girls</option>
                      <option value="co-ed">Co-ed</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <input
                    type="text"
                    className="form-input"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-input"
                      name="city"
                      value={formData.city}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-input"
                      name="state"
                      value={formData.state}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode</label>
                    <input
                      type="text"
                      className="form-input"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Latitude</label>
                    <input
                      type="number"
                      step="0.0001"
                      className="form-input"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Longitude</label>
                    <input
                      type="number"
                      step="0.0001"
                      className="form-input"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <a 
                      href={`https://www.google.com/maps/search/${encodeURIComponent(formData.address + ', ' + formData.city)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#4285f4',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      📍 Find on Google Maps
                    </a>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Total Rooms <span style={{color: '#999', fontSize: '12px'}}>(Auto-calculated from rooms added)</span></label>
                    <input
                      type="number"
                      className="form-input"
                      name="total_rooms"
                      value={formData.total_rooms}
                      readOnly
                      style={{backgroundColor: '#f5f5f5', cursor: 'not-allowed'}}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Monthly Rent (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="monthly_rent"
                      value={formData.monthly_rent}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Yearly Rent (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="yearly_rent"
                      value={formData.yearly_rent}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Deposit Amount (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="deposit_amount"
                      value={formData.deposit_amount}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Contact Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Email</label>
                    <input
                      type="email"
                      className="form-input"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Hostel Image</label>
                  <input
                    type="file"
                    className="form-input"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {imagePreview && <img src={imagePreview} alt="Preview" className="image-preview" />}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>Hostel Gallery Images (Multiple)</label>
                  <input
                    type="file"
                    className="form-input"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryChange}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
                    {existingGallery.map((url, idx) => (
                      <div key={`existing-${idx}`} style={{ position: 'relative', width: '100px', height: '100px' }}>
                        <img 
                          src={url.startsWith('http') ? url : `${API_BASE_URL.replace('/api', '')}${url}`} 
                          alt="Gallery" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} 
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingGalleryImage(url)}
                          style={{
                            position: 'absolute', top: '-6px', right: '-6px',
                            backgroundColor: '#ff4d4d', color: 'white', border: 'none',
                            borderRadius: '50%', width: '20px', height: '20px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '12px', fontWeight: 'bold'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {galleryPreviews.map((preview, idx) => (
                      <div key={`new-${idx}`} style={{ position: 'relative', width: '100px', height: '100px' }}>
                        <img 
                          src={preview} 
                          alt="Gallery Preview" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #90ee90' }} 
                        />
                        <button
                          type="button"
                          onClick={() => removeNewGalleryImage(idx)}
                          style={{
                            position: 'absolute', top: '-6px', right: '-6px',
                            backgroundColor: '#ff4d4d', color: 'white', border: 'none',
                            borderRadius: '50%', width: '20px', height: '20px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '12px', fontWeight: 'bold'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Services/Amenities</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <select
                      className="form-input"
                      value={newAmenity}
                      onChange={(e) => setNewAmenity(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select an amenity...</option>
                      {commonAmenities.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (newAmenity && !amenities.includes(newAmenity)) {
                          setAmenities([...amenities, newAmenity]);
                          setNewAmenity('');
                        }
                      }}
                      style={{ padding: '10px 20px' }}
                    >
                      Add
                    </button>
                  </div>
                  <div className="amenity-list">
                    {amenities.map(a => (
                      <div key={a} className="amenity-item">
                        {a}
                        <button
                          type="button"
                          className="amenity-remove"
                          onClick={() => setAmenities(amenities.filter(am => am !== a))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={uploading}
                    style={{ padding: '10px 20px' }}
                  >
                    {uploading ? 'Saving...' : editingHostelId ? 'Update Hostel' : 'Add Hostel'}
                  </button>
                  {editingHostelId && (
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={() => loadHostelRooms(selectedHostel || { id: editingHostelId })}
                      style={{ padding: '10px 20px' }}
                    >
                      Manage Rooms
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddForm(false);
                      setShowEditForm(false);
                      setFormData({
                        name: '',
                        address: '',
                        city: 'Nashik',
                        state: 'Maharashtra',
                        pincode: '',
                        latitude: '',
                        longitude: '',
                        hostel_type: 'boys',
                        total_rooms: '',
                        monthly_rent: '',
                        yearly_rent: '',
                        deposit_amount: '',
                        contact_phone: '',
                        contact_email: '',
                      });
                      setImageFile(null);
                      setImagePreview(null);
                      setGalleryFiles([]);
                      setGalleryPreviews([]);
                      setExistingGallery([]);
                      setAmenities([]);
                    }}
                    style={{ padding: '10px 20px' }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {showEditForm && (
            <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
              <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3 style={{ margin: 0 }}>Edit Hostel Location</h3>
                  <button className="modal-close" onClick={() => setShowEditForm(false)}>×</button>
                </div>

                <form onSubmit={handleEditHostel} style={{ padding: '20px' }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Hostel Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Hostel Type</label>
                      <select
                        className="form-input"
                        name="hostel_type"
                        value={formData.hostel_type}
                        onChange={handleFormChange}
                      >
                        <option value="boys">Boys</option>
                        <option value="girls">Girls</option>
                        <option value="co-ed">Co-ed</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Address *</label>
                    <input
                      type="text"
                      className="form-input"
                      name="address"
                      value={formData.address}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-input"
                        name="city"
                        value={formData.city}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className="form-input"
                        name="state"
                        value={formData.state}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Pincode</label>
                      <input
                        type="text"
                        className="form-input"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Latitude</label>
                      <input
                        type="number"
                        step="0.0001"
                        className="form-input"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Longitude</label>
                      <input
                        type="number"
                        step="0.0001"
                        className="form-input"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                      <a 
                        href={`https://www.google.com/maps/search/${encodeURIComponent(formData.address + ', ' + formData.city)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#4285f4',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '13px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        📍 Find on Google Maps
                      </a>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Total Rooms <span style={{color: '#999', fontSize: '12px'}}>(Auto-calculated)</span></label>
                      <input
                        type="number"
                        className="form-input"
                        name="total_rooms"
                        value={formData.total_rooms}
                        readOnly
                        style={{backgroundColor: '#f5f5f5', cursor: 'not-allowed'}}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Monthly Rent (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        name="monthly_rent"
                        value={formData.monthly_rent}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Yearly Rent (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        name="yearly_rent"
                        value={formData.yearly_rent}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Deposit Amount (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        name="deposit_amount"
                        value={formData.deposit_amount}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Contact Phone</label>
                      <input
                        type="tel"
                        className="form-input"
                        name="contact_phone"
                        value={formData.contact_phone}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Contact Email</label>
                      <input
                        type="email"
                        className="form-input"
                        name="contact_email"
                        value={formData.contact_email}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Hostel Image</label>
                    <input
                      type="file"
                      className="form-input"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview && <img src={imagePreview} alt="Preview" className="image-preview" />}
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: '600' }}>Hostel Gallery Images (Multiple)</label>
                    <input
                      type="file"
                      className="form-input"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryChange}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
                      {existingGallery.map((url, idx) => (
                        <div key={`existing-${idx}`} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img 
                            src={url.startsWith('http') ? url : `${API_BASE_URL.replace('/api', '')}${url}`} 
                            alt="Gallery" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} 
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingGalleryImage(url)}
                            style={{
                              position: 'absolute', top: '-6px', right: '-6px',
                              backgroundColor: '#ff4d4d', color: 'white', border: 'none',
                              borderRadius: '50%', width: '20px', height: '20px',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', fontWeight: 'bold'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {galleryPreviews.map((preview, idx) => (
                        <div key={`new-${idx}`} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img 
                            src={preview} 
                            alt="Gallery Preview" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #90ee90' }} 
                          />
                          <button
                            type="button"
                            onClick={() => removeNewGalleryImage(idx)}
                            style={{
                              position: 'absolute', top: '-6px', right: '-6px',
                              backgroundColor: '#ff4d4d', color: 'white', border: 'none',
                              borderRadius: '50%', width: '20px', height: '20px',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', fontWeight: 'bold'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Room Management Section */}
                  <div style={{ padding: '16px', backgroundColor: '#f0f8ff', borderRadius: '8px', marginBottom: '20px', border: '1px solid #b3d9ff' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#0066cc' }}>🛏️ Room-Wise Bed Management</h4>
                    
                    {/* Add New Room */}
                    <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #ddd' }}>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Add New Room</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', alignItems: 'flex-end' }}>
                        <div>
                          <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Building</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., A, B, Wing1"
                            value={newRoom.building}
                            onChange={(e) => setNewRoom({...newRoom, building: e.target.value})}
                            style={{ padding: '8px' }}
                          />
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Room #</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., 101, A1"
                            value={newRoom.room_number}
                            onChange={(e) => setNewRoom({...newRoom, room_number: e.target.value})}
                            style={{ padding: '8px' }}
                          />
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Beds</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="e.g., 2, 3, 4"
                            value={newRoom.capacity}
                            onChange={(e) => setNewRoom({...newRoom, capacity: e.target.value})}
                            style={{ padding: '8px' }}
                            min="1"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddRoomInEditForm}
                          style={{ padding: '8px 12px', fontSize: '12px' }}
                        >
                          + Add
                        </button>
                      </div>
                    </div>

                    {/* Existing Rooms List */}
                    <div>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                        Existing Rooms ({hostelRooms.length})
                      </h5>
                      {hostelRooms.length === 0 ? (
                        <p style={{ color: '#999', textAlign: 'center', padding: '12px', backgroundColor: '#fff', borderRadius: '6px', border: '1px dashed #ddd' }}>
                          No rooms added yet
                        </p>
                      ) : (
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {hostelRooms.map((room) => (
                            <div key={room.id} style={{ 
                              padding: '10px', 
                              backgroundColor: '#fff', 
                              borderRadius: '6px',
                              border: '1px solid #ddd',
                              display: 'grid',
                              gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                              gap: '8px',
                              alignItems: 'center'
                            }}>
                              <div>
                                <p style={{ margin: 0, fontWeight: '500', fontSize: '13px' }}>
                                  {room.building} - Room {room.room_number}
                                </p>
                              </div>
                              <div>
                                <label style={{ fontSize: '11px', color: '#666' }}>Beds:</label>
                                <input
                                  type="number"
                                  value={room.capacity}
                                  onChange={(e) => handleUpdateRoomBeds(room.id, e.target.value)}
                                  style={{ 
                                    width: '100%', 
                                    padding: '6px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                  }}
                                  min="1"
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: '11px', color: '#666' }}>Available:</label>
                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>{room.available_beds}</p>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <p style={{ margin: 0, fontSize: '11px', color: '#666' }}>Status</p>
                                <span style={{ 
                                  display: 'inline-block', 
                                  padding: '4px 8px', 
                                  backgroundColor: '#e6ffe6', 
                                  color: '#00aa00',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500'
                                }}>
                                  Active
                                </span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => handleDeleteRoomInEditForm(room.id)}
                                style={{ padding: '6px 10px', fontSize: '12px' }}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Services/Amenities</label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <select
                        className="form-input"
                        value={newAmenity}
                        onChange={(e) => setNewAmenity(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">Select an amenity...</option>
                        {commonAmenities.map(a => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          if (newAmenity && !amenities.includes(newAmenity)) {
                            setAmenities([...amenities, newAmenity]);
                            setNewAmenity('');
                          }
                        }}
                        style={{ padding: '10px 20px' }}
                      >
                        Add
                      </button>
                    </div>
                    <div className="amenity-list">
                      {amenities.map(a => (
                        <div key={a} className="amenity-item">
                          {a}
                          <button
                            type="button"
                            className="amenity-remove"
                            onClick={() => setAmenities(amenities.filter(am => am !== a))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={uploading}
                      style={{ padding: '10px 20px', flex: 1 }}
                    >
                      {uploading ? 'Saving...' : 'Update Hostel'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowEditForm(false);
                        setEditingHostelId(null);
                        setFormData({
                          name: '',
                          address: '',
                          city: 'Nashik',
                          state: 'Maharashtra',
                          pincode: '',
                          latitude: '',
                          longitude: '',
                          hostel_type: 'boys',
                          total_rooms: '',
                          monthly_rent: '',
                          yearly_rent: '',
                          deposit_amount: '',
                          contact_phone: '',
                          contact_email: '',
                        });
                         setImageFile(null);
                         setImagePreview(null);
                         setGalleryFiles([]);
                         setGalleryPreviews([]);
                         setExistingGallery([]);
                         setAmenities([]);
                      }}
                      style={{ padding: '10px 20px', flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="hostel-grid">
            {locations.length === 0 ? (
              <div className="empty">No hostel locations found.</div>
            ) : locations.map((hostel) => (
              <div key={hostel.id} className="hostel-card">
                <div className="hostel-card-img">
                  <CardImageSlider hostel={hostel} baseUrl={API_BASE_URL} />
                </div>
                <div className="hostel-card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <h4>{hostel.name}</h4>
                    <span className={`status-badge status-${hostel.hostel_type}`}>{hostel.hostel_type}</span>
                  </div>
                  <p>📍 {hostel.address}, {hostel.city}</p>
                  <p>🛏️ {hostel.available_beds} beds available • {hostel.total_rooms} rooms</p>
                  <div className="rating">⭐ {hostel.rating?.toFixed(1) || '0.0'} ({hostel.total_reviews || 0} reviews)</div>
                  <div className="price">
                    {formatCurrency(hostel.monthly_rent)}/month
                  </div>
                  {hostel.amenities && (
                    <div className="amenities">
                      {(typeof hostel.amenities === 'string' ? JSON.parse(hostel.amenities) : hostel.amenities).slice(0, 5).map((a, i) => (
                        <span key={i} className="amenity-tag">{a}</span>
                      ))}
                    </div>
                  )}
                  <p style={{ marginTop: 12 }}>📞 {hostel.contact_phone || 'N/A'}</p>
                  <div className="actions" style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <button
                      className="btn btn-info"
                      onClick={() => { setSelectedHostel(hostel); setShowDetailsModal(true); }}
                      style={{ padding: '8px 12px', fontSize: '11px' }}
                      title="View comprehensive hostel details"
                    >
                      Details
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => openEditForm(hostel)}
                      style={{ padding: '8px 12px', fontSize: '11px' }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => loadAmenities(hostel)}
                      style={{ padding: '8px 12px', fontSize: '11px' }}
                    >
                      Services
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDeleteHostel(hostel)}
                      style={{ padding: '8px 12px', fontSize: '11px', gridColumn: '1 / -1' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : loading ? (
        <div className="loading">Loading...</div>
      ) : activeTab === 'bookings' ? (
        <div className="table-container">
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '10px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '12px' }}>Start Date</label>
                <input
                  type="date"
                  value={bookingFilterStartDate}
                  onChange={(e) => setBookingFilterStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '12px' }}>End Date</label>
                <input
                  type="date"
                  value={bookingFilterEndDate}
                  onChange={(e) => setBookingFilterEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                />
              </div>
              <button
                onClick={() => { setBookingFilterStartDate(''); setBookingFilterEndDate(''); setBookingSearch(''); }}
                className="btn btn-secondary"
                style={{ padding: '8px 12px', fontSize: '12px' }}
              >
                Clear Filters
              </button>
              <button
                onClick={handlePrintToPDF}
                className="btn btn-info"
                style={{ padding: '8px 12px', fontSize: '12px' }}
              >
                📄 Print to PDF
              </button>
            </div>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="text"
                placeholder="Search by student name or phone..."
                value={bookingSearch}
                onChange={(e) => setBookingSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '260px' }}
              />
              {bookingSearch && <button onClick={() => setBookingSearch('')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px' }}>✕</button>}
              <span style={{ fontSize: '12px', color: '#666' }}>Showing {getFilteredBookings().length} of {bookingRequests.length} booking requests</span>
            </div>
          </div>
          <table className="data-table" ref={tableRef}>
            <thead>
              <tr><th>Sr. No.</th><th>Student</th><th>Hostel</th><th>Plan</th><th>Phone</th><th>Requested Date & Time</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {getFilteredBookings().map((booking, index) => (
                <tr key={booking.id}>
                  <td>{index + 1}</td>
                  <td>{booking.student_name}</td>
                  <td>{booking.hostel_name}</td>
                  <td>{booking.plan_type}</td>
                  <td>{booking.student_phone}</td>
                  <td>{formatDateTimeIST(booking.created_at)}</td>
                  <td><span className={`status-badge status-${booking.status}`}>{booking.status}</span></td>
                  <td className="actions">
                    {booking.status === 'pending' && (
                      <>
                        <button className="btn btn-success" onClick={() => handleBookingAction(booking.id, 'approved')}>Approve</button>
                        <button className="btn btn-danger" onClick={() => handleBookingAction(booking.id, 'rejected')}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'admissions' ? (
        <div className="table-container">
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', paddingRight: '16px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by student name..."
              value={admissionSearch}
              onChange={(e) => setAdmissionSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '260px' }}
            />
            {admissionSearch && <button onClick={() => setAdmissionSearch('')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px' }}>✕</button>}
            {admissionFilter && (
              <span style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                fontSize: '12px', 
                color: admissionFilter === 'active' ? '#15803d' : '#475569', 
                background: admissionFilter === 'active' ? '#f0fdf4' : '#fef2f2', 
                border: admissionFilter === 'active' ? '1px solid #bbf7d0' : '1px solid #fecaca', 
                borderRadius: '6px', 
                padding: '4px 10px' 
              }}>
                {admissionFilter === 'active' ? 'Hostel Active' : 'Subscription Ended'}
                <button onClick={() => setAdmissionFilter(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '14px', padding: '0 2px' }}>✕</button>
              </span>
            )}
            <span style={{ fontSize: '12px', color: '#666' }}>
              Showing {admissions.filter(a => {
                if (admissionFilter === 'ended' && (!a.valid_till || !isSubscriptionExpired(a.valid_till))) return false;
                if (admissionFilter === 'active' && (!a.valid_till || isSubscriptionExpired(a.valid_till))) return false;
                if (admissionSearch.trim() && !(a.student_name || '').toLowerCase().includes(admissionSearch.toLowerCase())) return false;
                return true;
              }).length} of {admissions.length}
            </span>
            <button
              onClick={handleSyncPaymentStatus}
              style={{ marginLeft: 'auto', padding: '7px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              title="Sync admissions where payment is verified but status is still pending"
            >
              🔄 Sync Payment Status
            </button>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Sr. No.</th><th>Student</th><th>Building</th><th>Room</th><th>Admission Date</th><th>Documents</th><th>Payment</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {admissions.filter(a => {
                if (admissionFilter === 'ended' && (!a.valid_till || !isSubscriptionExpired(a.valid_till))) return false;
                if (admissionFilter === 'active' && (!a.valid_till || isSubscriptionExpired(a.valid_till))) return false;
                if (admissionSearch.trim() && !(a.student_name || '').toLowerCase().includes(admissionSearch.toLowerCase())) return false;
                return true;
              }).map((admission, index) => (
                <tr key={admission.id}>
                  <td>{index + 1}</td>
                  <td>{admission.student_name || 'N/A'}</td>
                  <td>{admission.building_name || 'N/A'}</td>
                  <td>{admission.room_number || 'N/A'}</td>
                  <td>{formatDate(admission.admission_date)}</td>
                  <td>
                    <span className={`status-badge ${
                      admission.student_documents_verification_status === 'verified' ? 'status-active' :
                      admission.student_documents_verification_status === 'rejected' ? 'status-rejected' :
                      admission.student_documents_verification_status === 'pending' ? 'status-warning' :
                      'status-pending'
                    }`}>
                      {admission.student_documents_verification_status === 'verified' ? '✓ Verified' :
                       admission.student_documents_verification_status === 'rejected' ? '✗ Rejected' :
                       admission.student_documents_verification_status === 'pending' ? '⏳ Under Review' :
                       '⏳ Not Submitted'}
                    </span>
                  </td>
                  <td>
                    {admission.admission_status === 'payment_pending_verification' ? (
                      <span className={`status-badge status-warning`}>
                        🔄 {admission.payment_method || 'Cash'} - Awaiting Verification
                      </span>
                    ) : (
                      <span className={`status-badge ${admission.payment_completed ? 'status-active' : 'status-pending'}`}>
                        {admission.payment_completed ? '✓ Done' : '⏳ Pending'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ color: admission.valid_till ? '#ea580c' : '#6b7280', fontWeight: '500' }}>
                        {admission.valid_till ? formatDate(admission.valid_till) : '—'}
                      </span>
                      {admission.valid_till && isSubscriptionExpired(admission.valid_till) && (
                        <span style={{
                          background: '#fef2f2',
                          color: '#dc2626',
                          fontSize: '11px',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          border: '1px solid #fecaca',
                          display: 'inline-block'
                        }}>Subscription Ended</span>
                      )}
                      <span className={`status-badge ${
                        admission.status === 'cancelled' ? 'status-cancelled' :
                        admission.admission_status === 'completed' ? 'status-active' : 
                        admission.admission_status === 'documents_submitted' ? 'status-warning' :
                        admission.admission_status === 'payment_completed' ? 'status-warning' :
                        admission.admission_status === 'payment_pending_verification' ? 'status-warning' :
                        'status-pending'
                      }`}>
                        {admission.status === 'cancelled' ? 'Cancelled' :
                         admission.admission_status === 'completed' ? 'Confirmed' :
                         admission.admission_status === 'documents_submitted' ? 'Awaiting Confirmation' :
                         admission.admission_status === 'payment_completed' ? 'Awaiting Confirmation' :
                         admission.admission_status === 'payment_pending_verification' ? 'Payment Pending' :
                         'Pending'}
                      </span>
                    </div>
                  </td>
                  <td className="actions">
                    {admission.admission_status === 'payment_pending_verification' && (
                      <button 
                        className="btn btn-warning btn-small"
                        onClick={() => handleVerifyCashPayment(admission.id)}
                      >
                        Verify Cash Payment
                      </button>
                    )}
                    {admission.payment_completed && admission.admission_status !== 'completed' && admission.status !== 'cancelled' && admission.status !== 'inactive' && (
                      <button 
                        className="btn btn-success btn-small"
                        onClick={() => handleConfirmAdmission(admission.id)}
                      >
                        Confirm
                      </button>
                    )}
                    <button 
                      className="btn btn-info btn-small"
                      onClick={() => handleViewAdmissionDetails(admission.id)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'leaves' ? (
        <div className="table-container">
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
            <input
              type="text"
              placeholder="Search by student name or phone..."
              value={leaveSearch}
              onChange={(e) => setLeaveSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '260px' }}
            />
            {leaveSearch && <button onClick={() => setLeaveSearch('')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px' }}>✕</button>}
            <span style={{ fontSize: '12px', color: '#666' }}>
              Showing {leaves.filter(l => !leaveSearch.trim() || (l.student_name || '').toLowerCase().includes(leaveSearch.toLowerCase()) || (l.student_phone || '').toLowerCase().includes(leaveSearch.toLowerCase())).length} of {leaves.length}
            </span>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Sr. No.</th><th>Student</th><th>Phone</th><th>Room</th><th>Building</th><th>From Date</th><th>To Date</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {leaves.filter(l => !leaveSearch.trim() || (l.student_name || '').toLowerCase().includes(leaveSearch.toLowerCase()) || (l.student_phone || '').toLowerCase().includes(leaveSearch.toLowerCase())).map((leave, index) => (
                <tr key={leave.id}>
                  <td>{index + 1}</td>
                  <td>{leave.student_name || 'N/A'}</td>
                  <td>{leave.student_phone}</td>
                  <td>{leave.room_number || 'N/A'}</td>
                  <td>{leave.building_name || 'N/A'}</td>
                  <td>{formatDate(leave.from_date)}</td>
                  <td>{formatDate(leave.to_date)}</td>
                  <td><span className={`status-badge status-${leave.status}`}>{leave.status}</span></td>
                  <td className="actions">
                    {leave.status === 'pending' && (
                      <>
                        <button className="btn btn-success" onClick={() => handleLeaveAction(leave.id, 'approved')}>Approve</button>
                        <button className="btn btn-danger" onClick={() => handleLeaveAction(leave.id, 'rejected')}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {showAmenitiesModal && selectedHostel && (
        <div className="modal-overlay" onClick={() => setShowAmenitiesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Manage Services - {selectedHostel.name}</h3>
              <button className="modal-close" onClick={() => setShowAmenitiesModal(false)}>×</button>
            </div>

            <div className="form-group">
              <label className="form-label">Current Services</label>
              <div className="amenity-list">
                {amenities.map(a => (
                  <div key={a} className="amenity-item">
                    {a}
                    <button
                      className="amenity-remove"
                      onClick={() => handleRemoveAmenity(a)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Add New Service</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  className="form-input"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Select a service...</option>
                  {commonAmenities.filter(a => !amenities.includes(a)).map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                <button
                  className="btn btn-primary"
                  onClick={handleAddAmenity}
                  style={{ padding: '10px 20px' }}
                >
                  Add
                </button>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => setShowAmenitiesModal(false)}
              style={{ width: '100%', padding: '10px 20px', marginTop: '16px' }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {showRoomsModal && selectedHostel && (
        <div className="modal-overlay" onClick={() => setShowRoomsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Manage Rooms - {selectedHostel.name}</h3>
              <button className="modal-close" onClick={() => setShowRoomsModal(false)}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Add New Room Section */}
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 16px 0' }}>Add New Room</h4>
                
                <div className="form-group">
                  <label className="form-label">Building Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Building A, Wing 1"
                    value={newRoom.building}
                    onChange={(e) => setNewRoom({...newRoom, building: e.target.value})}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Room Number</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., 101, A1"
                      value={newRoom.room_number}
                      onChange={(e) => setNewRoom({...newRoom, room_number: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Number of Beds</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="e.g., 2, 3, 4"
                      value={newRoom.capacity}
                      onChange={(e) => setNewRoom({...newRoom, capacity: e.target.value})}
                    />
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleAddRoom}
                  style={{ width: '100%', padding: '10px 20px' }}
                >
                  Add Room
                </button>
              </div>

              {/* Rooms List */}
              <div>
                <h4 style={{ margin: '0 0 16px 0' }}>Rooms List ({hostelRooms.length})</h4>
                {hostelRooms.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No rooms added yet</p>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {hostelRooms.map((room) => (
                      <div key={room.id} style={{ 
                        padding: '12px', 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
                            {room.building || 'Unknown'} - Room {room.room_number}
                          </p>
                          <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                            🛏️ {room.capacity} beds | Available: {room.available_beds}
                          </p>
                        </div>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteRoom(room.id)}
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                className="btn btn-secondary"
                onClick={() => setShowRoomsModal(false)}
                style={{ width: '100%', padding: '10px 20px', marginTop: '20px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedHostel && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2 style={{ margin: 0, flex: 1 }}>{selectedHostel.name}</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Hostel Image */}
              <div style={{ marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', height: '300px' }}>
                <CardImageSlider hostel={selectedHostel} baseUrl={API_BASE_URL} />
              </div>

              {/* Basic Info */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '12px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>Basic Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>HOSTEL TYPE</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      <span className={`status-badge status-${selectedHostel.hostel_type}`}>{selectedHostel.hostel_type}</span>
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>RATING</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>⭐ {selectedHostel.rating?.toFixed(1) || '0.0'} ({selectedHostel.total_reviews || 0} reviews)</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>TOTAL ROOMS</p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>{selectedHostel.total_rooms || 0}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>AVAILABLE BEDS</p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>{selectedHostel.available_beds || 0}</p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '12px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>Address Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>ADDRESS</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>📍 {selectedHostel.address}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>CITY / STATE / PINCODE</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{selectedHostel.city}, {selectedHostel.state} {selectedHostel.pincode}</p>
                  </div>
                  {(selectedHostel.latitude && selectedHostel.longitude) && (
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>COORDINATES & MAP</p>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        📍 {selectedHostel.latitude}, {selectedHostel.longitude}
                      </p>
                      <a 
                        href={`https://www.google.com/maps?q=${selectedHostel.latitude},${selectedHostel.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                        style={{ marginTop: '8px', display: 'inline-block', padding: '6px 12px', textDecoration: 'none', fontSize: '12px' }}
                      >
                        📍 Open in Google Maps
                      </a>
                    </div>
                  )}
                  {(!selectedHostel.latitude || !selectedHostel.longitude) && (
                    <div style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '13px', fontWeight: 'bold' }}>⚠️ Coordinates Not Added</p>
                      <p style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '12px' }}>This hostel doesn't have location coordinates. Click "Edit" to add Latitude & Longitude using Google Maps.</p>
                      <button 
                        onClick={() => {
                          setShowEditForm(true);
                          setFormData({
                            name: selectedHostel.name,
                            address: selectedHostel.address,
                            city: selectedHostel.city,
                            state: selectedHostel.state,
                            pincode: selectedHostel.pincode,
                            latitude: selectedHostel.latitude || '',
                            longitude: selectedHostel.longitude || '',
                            hostel_type: selectedHostel.hostel_type,
                            total_rooms: selectedHostel.total_rooms || '',
                            monthly_rent: selectedHostel.monthly_rent || '',
                            yearly_rent: selectedHostel.yearly_rent || '',
                            deposit_amount: selectedHostel.deposit_amount || '',
                            contact_phone: selectedHostel.contact_phone || '',
                            contact_email: selectedHostel.contact_email || '',
                            image_url: selectedHostel.image_url,
                          });
                          if (selectedHostel.image_url) {
                            setImagePreview(selectedHostel.image_url.startsWith('http') ? selectedHostel.image_url : `${API_BASE_URL.replace('/api', '')}${selectedHostel.image_url}`);
                          }
                          setAmenities(typeof selectedHostel.amenities === 'string' ? JSON.parse(selectedHostel.amenities) : selectedHostel.amenities || []);
                        }}
                        style={{ padding: '6px 12px', backgroundColor: '#ffc107', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                      >
                        ✏️ Edit & Add Coordinates
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '12px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>Contact Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>PHONE</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>📞 {selectedHostel.contact_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>EMAIL</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>✉️ {selectedHostel.contact_email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Pricing Information */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '12px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>Pricing Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>MONTHLY RENT</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>{formatCurrency(selectedHostel.monthly_rent)}</p>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>YEARLY RENT</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>{formatCurrency(selectedHostel.yearly_rent)}</p>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>SECURITY DEPOSIT</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>{formatCurrency(selectedHostel.deposit_amount)}</p>
                  </div>
                </div>
              </div>

              {/* Services & Amenities */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '12px', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>Services & Amenities</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedHostel.amenities ? (
                    (typeof selectedHostel.amenities === 'string' ? JSON.parse(selectedHostel.amenities) : selectedHostel.amenities).map((amenity, idx) => (
                      <span key={idx} className="amenity-tag" style={{ padding: '6px 12px', backgroundColor: '#e7f3ff', border: '1px solid #007bff', borderRadius: '4px', fontSize: '13px' }}>
                        ✓ {amenity}
                      </span>
                    ))
                  ) : (
                    <p style={{ margin: 0, color: '#999' }}>No amenities listed</p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '20px' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => { setShowDetailsModal(false); openEditForm(selectedHostel); }}
                  style={{ padding: '10px 16px' }}
                >
                  Edit Details
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => { setShowDetailsModal(false); loadAmenities(selectedHostel); }}
                  style={{ padding: '10px 16px' }}
                >
                  Manage Services
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                  style={{ padding: '10px 16px' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDocumentsModal && selectedAdmission && (
        <div className="modal-overlay" onClick={() => setShowDocumentsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '95vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>� Admission Details - {selectedAdmission.student_name}</h3>
              <button className="modal-close" onClick={() => setShowDocumentsModal(false)}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Student Information */}
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Student Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Name</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{selectedAdmission.student_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Room</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{selectedAdmission.building_name} - Room {selectedAdmission.room_number}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Admission Date</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{formatDate(selectedAdmission.admission_date)}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Payment Status</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      {selectedAdmission.payment_completed ? '✓ Completed' : '⏳ Pending'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Verification Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#333' }}>💳 Payment Verification Status</h4>
                
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: selectedAdmission.payment_completed ? '#d1fae5' : '#fef3c7', 
                  border: `2px solid ${selectedAdmission.payment_completed ? '#10b981' : '#f59e0b'}`,
                  borderRadius: '8px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: selectedAdmission.payment_completed ? '#065f46' : '#92400e' }}>
                    {selectedAdmission.payment_completed ? '✓ Payment Verified' : '⏳ Payment Pending Verification'}
                  </p>
                </div>

                {/* Payment Details Table */}
                <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h5 style={{ margin: 0, fontSize: '13px', fontWeight: 'bold', color: '#333' }}>Payment Details:</h5>
                    <span style={{ 
                      display: 'inline-block', 
                      padding: '4px 10px', 
                      backgroundColor: selectedAdmission.plan_type === 'yearly' ? '#e0f7fa' : '#fff3e0',
                      color: selectedAdmission.plan_type === 'yearly' ? '#00796b' : '#e65100',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      border: `1px solid ${selectedAdmission.plan_type === 'yearly' ? '#80deea' : '#ffcc80'}`
                    }}>
                      📅 {selectedAdmission.plan_type === 'yearly' ? 'YEARLY PLAN' : 'MONTHLY PLAN'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedAdmission.plan_type === 'yearly' ? 'Yearly Rental Amount' : 'Monthly Rental Amount'}
                      </p>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
                        ₹{selectedAdmission.plan_type === 'yearly' 
                          ? (selectedAdmission.one_time_payment ? selectedAdmission.one_time_payment.toFixed(2) : '0.00')
                          : (selectedAdmission.monthly_fee ? selectedAdmission.monthly_fee.toFixed(2) : '0.00')
                        }
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Security Deposit Amount</p>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
                        ₹{selectedAdmission.deposit_amount ? selectedAdmission.deposit_amount.toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Payment Method</p>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        {selectedAdmission.payment_method ? selectedAdmission.payment_method.toUpperCase().replace(/_/g, ' ') : 'Not Specified'}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Transaction ID</p>
                      <p style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace' }}>
                        {selectedAdmission.transaction_id || selectedAdmission.payment_id || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Payment Date</p>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        {selectedAdmission.payment_date ? formatDateTimeIST(selectedAdmission.payment_date) : 'Not recorded'}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: 'bold' }}>Receipt/Screenshot</p>
                      {selectedAdmission.payment_screenshot_url ? (
                        <a 
                          href={selectedAdmission.payment_screenshot_url.startsWith('http') ? selectedAdmission.payment_screenshot_url : `${API_BASE_URL.replace('/api', '')}${selectedAdmission.payment_screenshot_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            padding: '6px 12px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          📷 View Receipt
                        </a>
                      ) : (
                        <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>No receipt attached</p>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#0369a1' }}>
                    ℹ️ <strong>Note:</strong> Documents have already been verified during student registration. Only payment verification is required for hostel admission.
                  </p>
                </div>
              </div>

              {/* Admission History */}
              <div style={{ marginTop: '24px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '15px', color: '#1e3a5f', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                  📋 Admission History
                </h4>
                {historyLoading ? (
                  <p style={{ color: '#6b7280', fontSize: '13px' }}>Loading history…</p>
                ) : admissionHistory.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '13px' }}>No history records found.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Event</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Period</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Rent</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Deposit</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Method</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Status</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Transaction ID</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admissionHistory.map((h, i) => {
                          const eventColors = {
                            new_admission: '#065f46', renewal: '#1d4ed8', payment: '#065f46',
                            payment_pending: '#92400e', payment_verified: '#065f46',
                            document_submitted: '#6b21a8', document_approved: '#065f46',
                            document_rejected: '#991b1b', cancellation: '#991b1b',
                          };
                          const eventLabels = {
                            new_admission: '🏠 New Admission', renewal: '🔄 Renewal',
                            payment: '✅ Payment', payment_pending: '⏳ Payment Pending',
                            payment_verified: '✔ Payment Verified', document_submitted: '📄 Docs Submitted',
                            document_approved: '✅ Docs Approved', document_rejected: '❌ Docs Rejected',
                            cancellation: '🚫 Cancellation',
                          };
                          return (
                            <tr key={h.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                              <td style={{ padding: '6px 8px', color: eventColors[h.event_type] || '#374151', fontWeight: '600' }}>
                                {eventLabels[h.event_type] || h.event_type}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#374151' }}>
                                {h.valid_from ? formatDate(h.valid_from) : '—'}
                                {h.valid_till ? ` → ${formatDate(h.valid_till)}` : ''}
                                {h.valid_till && isSubscriptionExpired(h.valid_till) && (
                                  <span style={{
                                    marginLeft: '6px',
                                    background: '#fef2f2',
                                    color: '#dc2626',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    padding: '1px 6px',
                                    borderRadius: '3px',
                                    border: '1px solid #fecaca'
                                  }}>Ended</span>
                                )}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#374151' }}>
                                {h.rent_amount ? `₹${Number(h.rent_amount).toLocaleString()}` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#374151' }}>
                                {h.deposit_amount ? `₹${Number(h.deposit_amount).toLocaleString()}` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#374151' }}>
                                {h.payment_method ? h.payment_method.replace('_', ' ') : '—'}
                              </td>
                              <td style={{ padding: '6px 8px' }}>
                                <span style={{
                                  background: h.payment_status === 'completed' ? '#d1fae5' : h.payment_status === 'pending' ? '#fef3c7' : '#fee2e2',
                                  color: h.payment_status === 'completed' ? '#065f46' : h.payment_status === 'pending' ? '#92400e' : '#991b1b',
                                  padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600'
                                }}>
                                  {(h.payment_status || '—').toUpperCase()}
                                </span>
                              </td>
                              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px', color: '#4b5563' }}>
                                {h.transaction_id || '—'}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                {h.created_at ? formatDate(h.created_at) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: selectedAdmission.admission_status === 'payment_pending_verification' ? '1fr 1fr' : '1fr', gap: '8px', marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '16px' }}>
                {selectedAdmission.admission_status === 'payment_pending_verification' && (
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to verify this payment? After verification, you can confirm the admission.')) {
                        handleVerifyCashPayment(selectedAdmission.id);
                        setShowDocumentsModal(false);
                      }
                    }}
                    style={{ padding: '10px 16px', fontSize: '14px', fontWeight: 'bold' }}
                  >
                    ✓ Verify Payment
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDocumentsModal(false)}
                  style={{ padding: '10px 16px', fontSize: '14px' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default HostelPage;
